import React, { useRef, useState } from "react";
import theme from "../data/theme";
import { MARKET_ANCHOR_PRESETS, MARKET_ANCHOR_CUSTOM } from "../utils/marketAnchor";

const { colors, spacing, radius, fontSize, fontWeight } = theme;

/**
 * MarketAnchor — two dollar questions about the top of the user's own market,
 * with platform presets in front of them.
 *
 * 🔴 CLEARING LANGUAGE ONLY. "usually goes for", "will sell for around" — never
 * "is worth", never "value", never "price". The board is NOT touched by this
 * control: stickers stay exactly where they were and only the BID-side soft cap
 * moves, so any copy implying the sticker changed would be a lie the user could
 * catch in one click on the player list. Real rooms clear away from sticker and
 * that gap IS the thing being calibrated.
 *
 * Same discipline as PositionDials: the readout underneath is the DERIVED
 * ladder from the engine's own fit (`predicted`, recomputed at the exact
 * (k, excessPass) that ships), not a restatement of what the user typed. If the
 * fit has to clamp to the measured hull, the echo is where the user sees it —
 * they typed $75 and the line says "will sell for around $68".
 *
 * This is the OVERPAY slider's visual weight without its labelling mistake
 * (that control is labelled `%` and applied as flat dollars).
 *
 * ── 🔴 THE PRESET CHIPS ARE A WRITER, NOT A MODE ────────────────────
 * Clicking Sleeper or Yahoo WRITES that preset's budget-scaled pair into the
 * two boxes as ordinary user values. There is no preset stored anywhere and no
 * preset reaches the engine: downstream of `onChange` a preset user and a user
 * who typed the same two numbers are the same user, byte for byte.
 *
 * `preset` comes in DERIVED from the pair actually in force
 * (`marketAnchorPresetFor`), so the lit chip is always a true statement about
 * what the room will bid — including on a fresh 1QB room, where nothing has
 * been typed and the format default IS the Sleeper pair.
 *
 * ── 🔴 THE RE-FILL RULE, AND WHY IT IS ON BLUR ──────────────────────
 * Emptying a box must not silently hand the user a DIFFERENT preset's number.
 * Without this rule a Yahoo user who cleared the top box would fall through the
 * per-field fallback to the FORMAT default — Sleeper's $70 — and the chip would
 * flip to Custom, because (70, 61) is neither preset. So an emptied box is
 * re-filled from the preset the user last picked, falling back to the format
 * default when they have picked none.
 *
 * ON BLUR, not on every keystroke, and that is deliberate: the box is
 * `type="number"` and backspacing to empty is how people retype a value. An
 * on-change re-fill would drop $73.50 back into the box mid-edit and make the
 * control impossible to type in. While focused an empty box stays empty — and
 * the echo underneath correctly shows the fallback it would take — and the
 * moment focus leaves, the value is made explicit. The stored pair therefore
 * never comes to rest empty for a user who picked a preset.
 */
export default function MarketAnchor({
  anchor, onChange, echo, formatDefault = null,
  /** Budget-scaled pairs, keyed like MARKET_ANCHOR_PRESETS. Null hides the row. */
  presetPairs = null,
  /** DERIVED by the caller from the pair in force — never stored. */
  preset = MARKET_ANCHOR_CUSTOM,
}) {
  const current = anchor || { rank5Price: null, topPrice: null };
  const touched = current.rank5Price != null || current.topPrice != null;

  // Transient, and deliberately NOT persisted: it exists only so an emptied box
  // can be re-filled from the preset the user actually clicked. On reload the
  // chip derives from the pair, which is the same answer by construction for
  // anyone who left a preset's numbers in place.
  const [lastPicked, setLastPicked] = useState(null);
  const topRef = useRef(null);

  // Both answered but the 5th is dearer than the top — almost always the two
  // boxes filled in the wrong order. The module already clamps to a sane (top ≥
  // 5th) ladder, so this is a NOTE, not an error: nothing is disabled, nothing
  // is blocked. It exists because the echo underneath will not match what they
  // typed, and that mismatch needs an explanation the user can act on.
  const inverted =
    current.rank5Price != null && current.topPrice != null &&
    current.rank5Price > current.topPrice;

  // Empty string clears the field back to null — that is how a field is emptied,
  // and it must round-trip through `null` rather than 0 (0 is not a positive
  // price and sanitize would reject it anyway, but a 0 sitting in the box reads
  // as an answer). The re-fill happens on blur, below.
  const set = (field, raw) => {
    const trimmed = String(raw).trim();
    const n = trimmed === "" ? null : Number(trimmed);
    onChange({ ...current, [field]: Number.isFinite(n) && n > 0 ? n : null });
  };

  /** The number an emptied box comes to rest on. Null = leave it empty. */
  const refillFor = (field) => {
    const picked = lastPicked && presetPairs ? presetPairs[lastPicked] : null;
    const src = picked || formatDefault;
    return src && src[field] != null ? src[field] : null;
  };

  const blur = (field) => {
    if (current[field] != null) return;
    const v = refillFor(field);
    if (v != null) onChange({ ...current, [field]: v });
  };

  const pick = (key) => {
    const pair = presetPairs && presetPairs[key];
    if (!pair) return;
    setLastPicked(key);
    onChange({ topPrice: pair.topPrice, rank5Price: pair.rank5Price });
  };

  // Custom is not a pair to apply — it is the state of having your own numbers.
  // Clicking it drops the remembered preset (so a later empty box falls back to
  // the room's own default rather than to a preset the user has moved off) and
  // puts the cursor in the first box, which is the action the chip stands for.
  const pickCustom = () => {
    setLastPicked(null);
    if (topRef.current) topRef.current.focus();
  };

  const chip = (key, label, onClick) => {
    const on = preset === key;
    return (
      <button
        key={key}
        type="button"
        onClick={onClick}
        aria-pressed={on}
        style={{
          background: on ? colors.brand.accent : "transparent",
          border: `1px solid ${on ? colors.brand.accent : colors.border.default}`,
          borderRadius: radius.xxl,
          color: on ? colors.bg.deep : colors.text.secondary,
          fontFamily: "inherit", fontSize: fontSize.micro,
          fontWeight: on ? fontWeight.bold : fontWeight.medium,
          padding: "4px 14px", cursor: "pointer", letterSpacing: "0.5px",
        }}
      >{label}</button>
    );
  };

  const activeProvenance =
    preset !== MARKET_ANCHOR_CUSTOM && MARKET_ANCHOR_PRESETS[preset]
      ? MARKET_ANCHOR_PRESETS[preset].provenance
      : null;

  const field = (key, label, question) => (
    <div>
      <label
        htmlFor={`market-anchor-${key}`}
        style={{
          display: "block", fontSize: fontSize.micro, fontWeight: fontWeight.bold,
          color: colors.text.secondary, letterSpacing: "1px", marginBottom: spacing.xs,
        }}
      >{label}</label>
      <div style={{ fontSize: fontSize.micro, color: colors.text.muted, lineHeight: 1.5, marginBottom: spacing.xs }}>
        {question}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: spacing.xs }}>
        <span style={{ fontSize: fontSize.body, color: colors.text.muted }}>$</span>
        <input
          id={`market-anchor-${key}`}
          ref={key === "topPrice" ? topRef : undefined}
          type="number"
          inputMode="decimal"
          min="1"
          step="0.5"
          placeholder={formatDefault && formatDefault[key] != null ? String(formatDefault[key]) : "—"}
          value={current[key] == null ? "" : current[key]}
          onChange={e => set(key, e.target.value)}
          onBlur={() => blur(key)}
          style={{
            flex: 1, padding: `${spacing.md}px`, borderRadius: radius.sm,
            fontSize: fontSize.body, fontWeight: fontWeight.medium, fontFamily: "inherit",
            background: colors.bg.deep, color: colors.text.primary,
            border: `1px solid ${colors.border.subtle}`,
          }}
        />
      </div>
    </div>
  );

  return (
    <div style={{ marginBottom: spacing.xxl }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: spacing.md, flexWrap: "wrap", gap: spacing.md,
      }}>
        <span style={{
          fontSize: fontSize.micro, fontWeight: fontWeight.bold,
          color: colors.text.secondary, letterSpacing: "1px",
        }}>TOP OF YOUR MARKET</span>
        {touched && (
          <button
            onClick={() => { setLastPicked(null); onChange({ rank5Price: null, topPrice: null }); }}
            style={{
              background: "transparent", border: `1px solid ${colors.border.default}`,
              borderRadius: radius.sm, color: colors.text.muted, fontFamily: "inherit",
              fontSize: fontSize.micro, padding: "3px 10px", cursor: "pointer",
            }}
          >Reset</button>
        )}
      </div>

      {presetPairs && (
        <div style={{ marginBottom: spacing.lg }}>
          <div style={{
            fontSize: fontSize.micro, color: colors.text.muted,
            lineHeight: 1.5, marginBottom: spacing.xs,
          }}>Where{"’"}s your real draft?</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: spacing.xs }}>
            {Object.values(MARKET_ANCHOR_PRESETS)
              .filter(p => presetPairs[p.key])
              .map(p => chip(p.key, p.label, () => pick(p.key)))}
            {chip(MARKET_ANCHOR_CUSTOM, "Custom", pickCustom)}
          </div>
          <div style={{
            marginTop: spacing.xs, fontSize: fontSize.micro,
            color: colors.text.faint, lineHeight: 1.5,
          }}>
            {activeProvenance || "Your own numbers — type them below."}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: spacing.lg }}>
        {/* Top price FIRST: it is the number people actually know off the top of
            their head, so it leads. "about" throughout — nobody knows this
            exactly and the copy should not pretend they do. */}
        {field("topPrice", "MOST EXPENSIVE",
          "Your league’s most expensive player usually goes for about…")}
        {field("rank5Price", "5TH MOST EXPENSIVE",
          "The 5th most expensive usually goes for about…")}
      </div>

      {inverted && (
        <div style={{
          marginTop: spacing.md, fontSize: fontSize.micro, lineHeight: 1.5,
          color: colors.status.warning,
        }}>
          Usually the most expensive is the higher number {"—"} double-check these?
        </div>
      )}

      <div style={{
        marginTop: spacing.lg, padding: `${spacing.md}px ${spacing.lg}px`,
        background: colors.bg.raised, borderRadius: radius.md,
        border: `1px solid ${colors.border.subtle}`,
      }}>
        {echo ? (
          <>
            <div style={{
              fontSize: fontSize.caption, color: colors.text.primary,
              fontVariantNumeric: "tabular-nums", marginBottom: spacing.xs,
            }}>
              Top player will sell for about{" "}
              <strong style={{ color: colors.brand.accent }}>${Math.round(echo.predicted.r1)}</strong>
              {" "}— 5th for about{" "}
              <strong style={{ color: colors.brand.accent }}>${Math.round(echo.predicted.r5)}</strong>.
            </div>
            <div style={{ fontSize: fontSize.micro, color: colors.text.muted, lineHeight: 1.5 }}>
              Listed prices don{"’"}t change {"—"} this moves how hard the room bids, so the
              top of the board clears where yours does.
            </div>
          </>
        ) : (
          <div style={{ fontSize: fontSize.micro, color: colors.text.faint, lineHeight: 1.5 }}>
            {touched
              ? "Needs both answers before it takes effect."
              : formatDefault
                ? "Using this room’s standard market. Pick a site above, or type your own numbers, and the room will bid so the top of the board clears at your prices."
                : "Leave these empty for the standard market. Fill both in and the room will bid so the top of the board clears at your prices."}
          </div>
        )}
      </div>
    </div>
  );
}
